import {
  throttle,
  debounce,
  touchHandler,
  getCanvasBox,
  getMouseLocation,
  getCursor,
  getTransformedCoordinates,
  drawTransformed,
  drawImage,
  drawSelectionBackdrop,
  drawRuleOfThirds,
  drawResizeHandles
} from 'modules/ImageCrop/util';
import EventEmitter from 'event-emitter';

/**
 * Class for cropping images using canvas
 *
 * @class ImageCrop                   - Manages image cropping, rotating, flipping and saving.
 * @param {CanvasImageSource} image   - HTMLImageElement, HTMLVideoElement, HTMLCanvasElement, or ImageBitmap.
 * @param {object}            options - Options for this instance of ImageCrop.
 */
export default class ImageCrop {
  static _defaultOptions = {
    initialFill      : 'transparent',
    activeFill       : 'rgba(0, 0, 0, 0.5)',
    ratio            : null,
    handleSize       : 15,
    handleFill       : 'rgba(0, 0, 0, 0.65)',
    keyboard         : true,
    keyboardStep     : 5,
    dragThreshold    : 0,
    ruleOfThirds     : true,
    ruleOfThirdsFill : 'rgba(0, 0, 0, 0.5)'
  };

  constructor(image, settings) {
    if (!(this instanceof ImageCrop)) {
      return new ImageCrop(image, settings);
    }

    // Provide settings to the instance
    this.set(settings);

    // Store base application state
    this._initialized = false;

    // Store the initial crop coordinates
    this._cropWindow = {
      x: 0,
      y: 0,
      width: 0,
      height: 0
    };

    // Store original state of transformations
    this.flipped = false;
    this.flopped = false;
    this.rotation = 0;

    // Initialize the plugin with the given image
    this.baseImage = image;

    // Optionally initialize the plugin if the canvas already exists
    const options = this.getOptions();

    if (options.canvas && options.canvas.base && options.canvas.selection) {
      this.init({
        base: options.canvas.base,
        selection: options.canvas.selection
      });
    }
  }

  /**
   * Return the object containing options
   * If one doesn't exist, set them to the default
   *
   * @return {object} - Options currently available for this instance
   */
  getOptions() {
    return this._options;
  }

  /**
   * Create the canvas elements and assign them to globals
   *
   * @param  {object|object[]} layer - Layer name as a string, or an array of strings
   */
  createLayer(layer, draw) {
    const options = this.getOptions();

    // Set the canvas object if it isn't set
    if (!this.layer) {
      this.layer = {};
    }

    // Set the layer object if it isn't set
    if (!this.layer[layer.name]) {
      this.layer[layer.name] = {};
    }

    // Create the canvas element
    this.layer[layer.name].enabled = true;
    this.layer[layer.name].events = [];
    this.layer[layer.name].canvas = options.canvas[layer.name];
    this.layer[layer.name].ctx = this.layer[layer.name].canvas.getContext('2d');
    this.layer[layer.name].draw = (...args) => {
      if (this.layer[layer.name].enabled) {
        draw(...args);
      } else {
        this.layer[layer.name].ctx.clearRect(
          0,
          0,
          this.layer[layer.name].canvas.width,
          this.layer[layer.name].canvas.height
        );
      }
    };

    // If we're allowing keyboard controls, the canvas needs to be targetable
    if (layer.targetable) {
      this.layer[layer.name].canvas.setAttribute('tabindex', '1');
      this.layer[layer.name].canvas.style.outline = 'none';
    }

    return this;
  }

  /**
   * Create a canvas to signify the selection
   * Handle collisions and ratios
   */
  initSelection() {
    const options = this.getOptions();

    // Set up and position the selection layer canvas
    this.createLayer(
      { name:'selection', targetable: options.keyboard },
      (layer, drawParameters) => {
        const { imageWidth, imageHeight } = layer;
        const ratio = options.ratio || drawParameters.ratio;
        const cropWindow = this.getCropWindow(true);

        // If the selection is larger than the threshold, draw the selection
        if (Math.min(Math.abs(cropWindow.width), Math.abs(cropWindow.height)) > options.dragThreshold) {

          // Prevent the crop box from going out of bounds
          if (cropWindow.x < 0) {
            cropWindow.x = 0;
          }
          if (cropWindow.y < 0) {
            cropWindow.y = 0;
          }
          if (cropWindow.x > imageWidth) {
            cropWindow.x = imageWidth
          }
          if (cropWindow.y > imageHeight) {
            cropWindow.y = imageHeight
          }

          // When dragging, prevent the box from being pushed off the bottom and right edges
          if (drawParameters.keyboard || drawParameters.currentMouseState === 'dragging') {

            if (cropWindow.x + cropWindow.width > imageWidth) {
              cropWindow.x = imageWidth - cropWindow.width;
            }
            if (cropWindow.y + cropWindow.height > imageHeight) {
              cropWindow.y = imageHeight - cropWindow.height;
            }
          }

          // Make sure the width and height are maxed out if we are drawing or resizing
          // Note: this code should not be running when doing any keyboard interactions
          if (!drawParameters.keyboard && drawParameters.currentMouseState !== 'dragging') {

            if (Math.abs(cropWindow.width) > imageWidth) {
              cropWindow.width = imageWidth * (cropWindow.width < 0 ? -1 : 1);
            }
            if (Math.abs(cropWindow.height) > imageHeight) {
              cropWindow.height = imageHeight * (cropWindow.height < 0 ? -1 : 1);
            }
            if (cropWindow.width < 0 && cropWindow.x + cropWindow.width < 0) {
              cropWindow.width = cropWindow.x * -1;
            }
            if (cropWindow.height < 0 && cropWindow.y + cropWindow.height < 0) {
              cropWindow.height = cropWindow.y * -1;
            }
            if (cropWindow.x + cropWindow.width > imageWidth) {
              cropWindow.width = imageWidth - cropWindow.x;
            }
            if (cropWindow.y + cropWindow.height > imageHeight) {
              cropWindow.height = imageHeight - cropWindow.y;
            }

            if (ratio) {
              const sideLength = Math.min(Math.abs(cropWindow.width) / ratio, Math.abs(cropWindow.height));

              cropWindow.width = sideLength * (cropWindow.width < 0 ? -1 : 1) * ratio;
              cropWindow.height = sideLength * (cropWindow.height < 0 ? -1 : 1);
            }
          }

          // Set the crop window
          this.setCropWindow(cropWindow, true);

          // Clear everything on the canvas
          layer.ctx.clearRect(0, 0, layer.canvas.width, layer.canvas.height);

          drawTransformed(layer, {
            flipped: this.flipped,
            flopped: this.flopped,
            rotation: this.rotation
          }, () => {
            const cropWindow = this.getCropWindow(true);

            // Draw a dark backdrop on everything but the selection
            layer.ctx.fillStyle = options.activeFill;
            drawSelectionBackdrop(layer, cropWindow);

            // Optionally draw rule of thirds lines
            layer.ctx.fillStyle = options.ruleOfThirdsFill;
            if (options.ruleOfThirds) {
              drawRuleOfThirds(layer, cropWindow);
            }

            // Draw corner resize handles
            layer.ctx.fillStyle = options.handleFill;
            drawResizeHandles(layer, cropWindow, {
              handleSize: options.handleSize,
              ratio
            });
          });
        }

        // If the selection is too small, clear the selection
        else {
          layer.ctx.clearRect(0, 0, layer.canvas.width, layer.canvas.height);
        }
      }
    );
  }

  /**
   * Initialize mouse and keyboard events
   */
  initEvents() {
    const options = this.getOptions();
    const layer = this.layer.selection;
    const canvas = layer.canvas;
    const dragCoords = {};
    const drawParameters = {};
    let canvasBox = getCanvasBox(canvas);
    let currentMouseState, mouseLocation;

    const updateCanvasBox = debounce(() => {
      canvasBox = getCanvasBox(canvas);
    }, 16);

    // Update the canvas box when the window resizes
    layer.events.push({
      elem: window,
      event: 'resize',
      handler: updateCanvasBox
    });

    // Allow changing selection position with keyboard
    if (options.keyboard) {
      layer.events.push({
        elem: canvas,
        event: 'keydown',
        handler: (e) => {
          const cropWindow = this.getCropWindow(true);
          let stepValue = options.keyboardStep;
          let horizontal = 0;
          let vertical = 0;

          if (e.keyCode >= 37 && e.keyCode <= 40) {

            // Allow faster movement if shift key is down
            if (e.shiftKey) {
              stepValue *= 10;
            }

            // Left
            if (e.keyCode === 37) {
              horizontal = stepValue * -1;
            }

            // Up
            else if (e.keyCode === 38) {
              vertical = stepValue * -1;
            }

            // Right
            else if (e.keyCode === 39) {
              horizontal = stepValue;
            }

            // Down
            else if (e.keyCode === 40) {
              vertical = stepValue;
            }

            // Adjust the selection
            if (this.rotation === 90) {
              cropWindow.x += vertical * (this.flopped ? -1 : 1);
              cropWindow.y += horizontal * (this.flipped ? 1 : -1);
            } else if (this.rotation === 180) {
              cropWindow.x += horizontal * (this.flipped ? 1 : -1);
              cropWindow.y += vertical * (this.flopped ? 1 : -1);
            } else if (this.rotation === 270) {
              cropWindow.x += vertical * (this.flopped ? 1 : -1);
              cropWindow.y += horizontal * (this.flipped ? -1 : 1);
            } else {
              cropWindow.x += horizontal * (this.flipped ? -1 : 1);
              cropWindow.y += vertical * (this.flopped ? -1 : 1);
            }

            e.preventDefault();
            e.stopPropagation();
          }

          // Clear the selection when hitting the Esc key
          else if (e.keyCode === 27) {
            cropWindow.x = 0;
            cropWindow.y = 0;
            cropWindow.width = 0;
            cropWindow.height = 0;

            e.preventDefault();
            e.stopPropagation();
          }

          // Set the crop window
          this.setCropWindow(cropWindow, true);

          this.draw('selection', { keyboard: true });
        }
      });
    }

    // update the cursor and drag coordinates based on mouse movements
    layer.events.push({
      elem: document,
      event: 'mousemove',
      handler: throttle((ev) => {

        // The instance has been destroyed, don't try to do work
        if (!this.layer) {
          return;
        }

        updateCanvasBox();
        const cropWindow = this.getCropWindow(true);
        const { canvasX, canvasY } = getTransformedCoordinates(this.layer.selection, {
          canvasBox,
          pageX: ev.pageX,
          pageY: ev.pageY,
          rotation: this.rotation,
          flipped: this.flipped,
          flopped: this.flopped
        });

        if (!currentMouseState) {
          mouseLocation = getMouseLocation({
            canvasX,
            canvasY,
            cropWindow,
            ratio: options.ratio,
            handleSize: options.handleSize
          });
        }

        if (currentMouseState === 'resizing') {
          cropWindow.x = dragCoords.x;
          cropWindow.y = dragCoords.y;
        }

        if (currentMouseState === 'resizing' || currentMouseState === 'drawing') {

          if (mouseLocation === 'n-resize' || mouseLocation === 's-resize') {
            cropWindow.height = canvasY - cropWindow.y;
          } else if (mouseLocation === 'w-resize' || mouseLocation === 'e-resize') {
            cropWindow.width = canvasX - cropWindow.x;
          } else {
            cropWindow.width = canvasX - cropWindow.x;
            cropWindow.height = canvasY - cropWindow.y;
          }

          // If shift key is held, keep a ratio
          if (ev.shiftKey && !drawParameters.ratio) {
            drawParameters.ratio = Math.abs((canvasX - cropWindow.x) / (canvasY - cropWindow.y));
          } else if (!ev.shiftKey) {
            drawParameters.ratio = null;
          }
        } else if (currentMouseState === 'dragging') {
          cropWindow.x = canvasX - dragCoords.mouseX;
          cropWindow.y = canvasY - dragCoords.mouseY;
        }

        // Set the crop window
        this.setCropWindow(cropWindow, true);

        if (currentMouseState) {
          // draw the selection box
          this.draw('selection', {
            currentMouseState,
            ...drawParameters
          });
        } else {
          canvas.style.cursor = getCursor(mouseLocation, {
            flipped: this.flipped,
            flopped: this.flopped,
            rotation: this.rotation
          });
        }
      }, 16)
    });

    // Store the original coordinates based on where the user is clicking
    layer.events.push({
      elem: canvas,
      event: 'mousedown',
      handler: (ev) => {
        const cropWindow = this.getCropWindow(true);
        const { canvasX, canvasY } = getTransformedCoordinates(this.layer.selection, {
          canvasBox,
          pageX: ev.pageX,
          pageY: ev.pageY,
          rotation: this.rotation,
          flipped: this.flipped,
          flopped: this.flopped
        });

        // Prevent selections when moving the mouse around
        ev.preventDefault();
        ev.target.focus();

        mouseLocation = getMouseLocation({
          canvasX,
          canvasY,
          cropWindow,
          ratio: options.ratio,
          handleSize: options.handleSize
        });

        // Check to see if we're resizing
        if (mouseLocation.indexOf('resize') > -1) {
          dragCoords.x = cropWindow.x;
          dragCoords.y = cropWindow.y;
          dragCoords.height = cropWindow.height;
          dragCoords.width = cropWindow.width;

          if (mouseLocation === 'nw-resize') {
            dragCoords.x = cropWindow.x + cropWindow.width;
            dragCoords.y = cropWindow.y + cropWindow.height;
            dragCoords.height = cropWindow.height * -1;
            dragCoords.width = cropWindow.width * -1;
          } else if (mouseLocation === 'ne-resize') {
            dragCoords.y = cropWindow.y + cropWindow.height;
            dragCoords.height = cropWindow.height * -1;
          } else if (mouseLocation === 'sw-resize') {
            dragCoords.x = cropWindow.x + cropWindow.width;
            dragCoords.width = cropWindow.width * -1;
          } else if (mouseLocation === 'n-resize') {
            dragCoords.y = cropWindow.y + cropWindow.height;
            dragCoords.height = cropWindow.height * -1;
          } else if (mouseLocation === 'w-resize') {
            dragCoords.x = cropWindow.x + cropWindow.width;
            dragCoords.width = cropWindow.width * -1;
          }

          currentMouseState = 'resizing';
        } else if (mouseLocation === 'selection') {
          // set the starting point for our drag
          dragCoords.x = cropWindow.x;
          dragCoords.y = cropWindow.y;
          dragCoords.mouseX = canvasX - cropWindow.x;
          dragCoords.mouseY = canvasY - cropWindow.y;

          currentMouseState = 'dragging';
        } else {
          // set initial top and left coordinates
          cropWindow.x = canvasX;
          cropWindow.y = canvasY;
          cropWindow.width = 0;
          cropWindow.height = 0;

          currentMouseState = 'drawing';
        }

        // Set the crop window
        this.setCropWindow(cropWindow, true);
      }
    });

    // When manipulation is over, remake the selection with a top left origin
    layer.events.push({
      elem: document,
      event: 'mouseup',
      handler: () => {
        const cropWindow = this.getCropWindow(true);

        if (cropWindow.width < 0) {
          cropWindow.width = Math.abs(cropWindow.width);
          cropWindow.x -= cropWindow.width;
        }

        if (cropWindow.height < 0) {
          cropWindow.height = Math.abs(cropWindow.height);
          cropWindow.y -= cropWindow.height;
        }

        // Set the crop window
        this.setCropWindow(cropWindow, true);

        currentMouseState = null;
        mouseLocation = null;

        // do a final draw with all available options
        drawParameters.ratio = null;
        this.draw('selection', drawParameters);
      }
    });

    // Translate touch events to mouse events
    layer.events.push({
      elem: canvas,
      event: 'touchstart',
      handler: touchHandler
    });
    layer.events.push({
      elem: canvas,
      event: 'touchend',
      handler: touchHandler
    });
    layer.events.push({
      elem: canvas,
      event: 'touchmove',
      handler: touchHandler
    });

    // Finally, add all of the events
    layer.events.forEach((ev) => this.addEvent(layer, ev));
  }

  /**
   * Add an event listener to the given object
   */
  addEvent(layer, { elem, event, handler }) {

    // Wrap the event handler so we can prevent calling it when the layer is disabled
    handler._wrapper = (...args) => {

      if (layer.enabled) {
        handler(...args);
      }
    };

    elem.addEventListener(event, handler._wrapper, true);
  }

  /**
   * Remove an event listener from the given object
   */
  removeEvent({ elem, event, handler }) {
    if (handler._wrapper) {
      elem.removeEventListener(event, handler._wrapper, true);
    } else {
      elem.removeEventListener(event, handler, true);
    }
  }

  /**
   * Remove event listeners from objects so we can garbage collect
   */
  destroyEvents() {

    for (let layer in this.layer) {
      this.layer[layer].events.forEach(this.removeEvent);
      this.layer[layer].events = [];
    }
  }

  /**
   * Create a canvas to replace the original image
   * Create another canvas that will represent the selection
   * Set globals so the canvases can be accessed
   */
  init = ({ base, selection }) => {

    if (this._initialized) {
      return;
    }
    this._initialized = true;

    const options = this.getOptions();

    // Add the canvi to the options for later access
    this.set('canvas', { base, selection });

    // Set up and position the base layer canvas
    this.createLayer(
      { name: 'base' },
      (layer, drawParameters) => {
        // Clear everything on the canvas
        layer.ctx.clearRect(0, 0, layer.canvas.width, layer.canvas.height);

        // Draw the image and take transformations into account
        drawImage(layer, {
          image: this.image,
          ...drawParameters
        });

        // Draw a light backdrop on the image
        layer.ctx.fillStyle = options.initialFill;
        layer.ctx.fillRect(0, 0, layer.canvas.width, layer.canvas.height);
      }
    );

    // Init the selection layer
    this.initSelection();

    // Set the starting image and draw everything
    this.setImage(this.baseImage);
    this.draw();

    // Initialize user events after the image is drawn
    this.initEvents();

    this.emit('initialized');
  };

  /**
   * Set the image of the base canvas and do all calculations necessary for the rest of
   * the app to work.
   *
   * @param {CanvasImageSource} image
   */
  setImage = (image) => {
    const options = this.getOptions();

    this.image = image;

    // Store the amount we scale the image
    this.scaleFactor = Math.max(
      Math.max(this.image.height, options.canvas.base.height) / options.canvas.base.height,
      Math.max(this.image.width, options.canvas.base.width) / options.canvas.base.width
    );

    // Calculate the scaled image size and position
    const imageWidth = this.image.width / this.scaleFactor;
    const imageHeight = this.image.height / this.scaleFactor;
    const imageX = (options.canvas.base.width / 2) - (imageWidth / 2);
    const imageY = (options.canvas.base.height / 2) - (imageHeight / 2);

    // Add these properties to every layer
    for (let layerName in this.layer) {
      this.layer[layerName].imageX = imageX;
      this.layer[layerName].imageY = imageY;
      this.layer[layerName].imageWidth = imageWidth;
      this.layer[layerName].imageHeight = imageHeight;
    }

    return this;
  };

  /**
   * Draw the canvases, if a layer is specified, only draw that layer
   *
   * @param {(string|string[])} [layer] - Layer name as a string, or an array of layers
   * @param {object} [drawParameters]   - Parameters used for drawing
   */
  draw = (layer, drawParameters = {}) => {

    // If layer is an array, then loop through and draw all layers
    if (layer instanceof Array) {
      layer.forEach((elem) => this.draw(elem, drawParameters));
    }

    // If no layer is specified, draw all layers
    else if (!layer) {
      for (layer in this.layer) {
        this.draw(layer, drawParameters);
      }
    }

    // Draw the canvas for the named layer
    else {
      requestAnimationFrame(() => {

        // Don't attempt to draw if the plugin has been destroyed
        if (!this.layer) {
          return;
        }

        this.layer[layer].draw(this.layer[layer], {
          flipped: this.flipped,
          flopped: this.flopped,
          rotation: this.rotation,
          ...drawParameters
        });
      });
    }

    return this;
  };

  /**
   * Get the value of the passed prop
   *
   * @param {string} prop - String of an option key
   */
  get = (prop) => this._options[prop];

  /**
   * Update the options with the passed parameter(s)
   * If the options global is not set, set it to the default options
   * If the first parameter is an object, pass any new parameters to the options global
   * If the first parameter is a string, update the option using the first parameter as a key
   *
   * @param {(object|string)} prop - Object of options, or string of an option key
   * @param {*} [value]      - Value to set if the first parameter is a string
   */
  set = (prop, value) => {

    // Set the options to the defaults if they aren't currently set
    if (!this._options) {
      this._options = Object.assign({}, ImageCrop._defaultOptions);
    }

    // if an object is passed as the first parameter, extend the options object with it
    if (typeof prop === 'object') {
      Object.assign(this._options, prop);
    }

    // otherwise assume we were given a property/value and update it
    else if (prop) {
      this._options[prop] = value;
    }

    return this.draw();
  };

  /**
   * Flip the image horizontally about the center.
   */
  flip = () => {
    this.flipped = !this.flipped;

    return this.draw();
  };

  /**
   * Flip the image vertically about the center.
   */
  flop = () => {
    this.flopped = !this.flopped;

    return this.draw();
  };

  /**
   * Rotate the image by a given amount. Stick to multiples of 90 so life is easier.
   *
   * @param {Number} degrees - A number of degrees to rotate the image.
   */
  rotate = (degrees) => {

    // We can only rotate 90 degrees at a time
    if (typeof degrees !== 'number' || degrees % 90 !== 0) {
      throw new Error('`degrees` must be a multiple of `90`');
    }

    // Make the number positive
    while (degrees < 0) {
      degrees += 360;
    }

    // Make the number one of four possibilities: 0, 90, 180, 270
    this.rotation = degrees % 360;

    this.emit('imageRotated', this.rotation);

    return this.draw();
  };

  /**
   * Get the current crop window coordinates.
   *
   * @param  {boolean} [scaled] - Whether you want the scaled down version of
   *                              the crop window coordinates.
   * @return {object}           - x, y, width, and height of the crop window
   */
  getCropWindow = (scaled) => ({
    x: this._cropWindow.x * (scaled ? 1 : this.scaleFactor),
    y: this._cropWindow.y * (scaled ? 1 : this.scaleFactor),
    width: this._cropWindow.width * (scaled ? 1 : this.scaleFactor),
    height: this._cropWindow.height * (scaled ? 1 : this.scaleFactor)
  });

  /**
   * Set the crop window to the given coordinates. You will need to call
   * `draw` after your manipulations are complete.
   *
   * @param  {object}  cropWindow - The x, y, width, and height of the new crop window.
   * @param  {boolean} [scaled]   - Whether you are setting the scaled down version of
   *                                the crop window coordinates.
   */
  setCropWindow = (cropWindow, scaled) => {

    if (typeof cropWindow.x !== 'undefined') {
      this._cropWindow.x = cropWindow.x / (scaled ? 1 : this.scaleFactor);
    }
    if (typeof cropWindow.y !== 'undefined') {
      this._cropWindow.y = cropWindow.y / (scaled ? 1 : this.scaleFactor);
    }
    if (typeof cropWindow.width !== 'undefined') {
      this._cropWindow.width = cropWindow.width / (scaled ? 1 : this.scaleFactor);
    }
    if (typeof cropWindow.height !== 'undefined') {
      this._cropWindow.height = cropWindow.height / (scaled ? 1 : this.scaleFactor);
    }

    // Emit an event so subscribers know that the crop window changed
    this.emit('cropWindowChanged', this.getCropWindow());

    return this;
  };

  /**
   * Get the current image size.
   *
   * @return {object} - width and height of the currently displayed image
   */
  getImageSize = () => ({
    width: Math.round(this.layer.base.imageWidth * this.scaleFactor),
    height: Math.round(this.layer.base.imageHeight * this.scaleFactor)
  });

  /**
   * Apply the crop so the base image is now larger than it was before. Store
   * the crop coordinates and allow more cropping on the now smaller portion of
   * the image and maintain coordinates relative to the full image.
   */
  applyCrop = () => {
    const cropWindow = this.getCropWindow();
    const options = this.getOptions();

    // If the selection is smaller than the threshold, we can't apply anything
    if (Math.min(Math.abs(cropWindow.width), Math.abs(cropWindow.height)) <= options.dragThreshold) {
      return;
    }

    // Create a canvas which we will draw the small image on
    const canvas = document.createElement('canvas');
    canvas.width = cropWindow.width;
    canvas.height = cropWindow.height;

    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      this.image,
      cropWindow.x,
      cropWindow.y,
      cropWindow.width,
      cropWindow.height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    this
      .setImage(canvas)
      .setCropWindow({
        x: 0,
        y: 0,
        width: 0,
        height: 0
      })
      .draw();

    // Emit the event after the image is set
    this.emit('cropApplied', cropWindow);

    return this;
  };

  /**
   * Reset the image so it looks uncropped and reset the crop coordinates.
   */
  resetCrop = () => {

    this
      .setImage(this.baseImage)
      .setCropWindow({
        x: 0,
        y: 0,
        width: 0,
        height: 0
      })
      .draw();

    // Emit the event after the image is set
    this.emit('cropReset');

    return this;
  };

  /**
   * Hide a given canvas from rendering and disable all associated events.
   *
   * @param {string} layerName - The name of the canvas to disable.
   */
  disable = (layerName) => {
    this.layer[layerName].enabled = false;
    this.layer[layerName].canvas.style.cursor = 'auto';

    return this.draw();
  };

  /**
   * Enable the given canvas and all associated events.
   *
   * @param {string} layerName - The name of the canvas to enable.
   */
  enable = (layerName) => {
    this.layer[layerName].enabled = true;

    return this.draw();
  };

  /**
   * Destroy the plugin to avoid memory leaks
   */
  destroy = () => {
    this.destroyEvents();
    this.baseImage = null;
    this.image = null;
    this.layer = null;
  };
}

// Initialize the event emitter on all classes
EventEmitter(ImageCrop.prototype);



// WEBPACK FOOTER //
// ./src/modules/ImageCrop/ImageCrop.js